"use client";

import { useContext } from "react";
import getFormDetailsHandler from "@/interface/api/GetFormDetails";
import { buildAddons } from "@/utils/addons";
import { GlobalContext } from "@/contexts/SiteDataContext";
import { getSlugByPdfId } from "@/utils/Form/formUtils";
import { Addon } from "@/templates/types";
import { useApi } from "./useApi";
import useGetOrder from "./useGetOrder";
import * as log from "@/actions/logger";

interface addonProps {
  amount: number;
  description: string;
  errorMessage: string;
  id: string;
  identifier: "discount" | "insurance" | "card" | "priority" | "tsa_precheck";
  name: string;
  order: number;
  price: string;
  required: boolean;
  title: string;
  tooltip: string;
  type: string;
  validate: boolean;
  value: string;
}

type PDF = {
  deactivated: boolean;
  id: string;
  name: string;
  description: string;
  price: {
    name: string;
    amount: number;
    government_fee: number;
    total: number;
  };
};

export type FormDetails = {
  form: Record<string, any> | null;
  addons: addonProps[];
  pdf: PDF | null;
  price: number | null;
  total: number | null;
};

const useGetFormDetails = () => {
  const { siteData } = useContext(GlobalContext).context;
  const { getOrder } = useGetOrder();
  const { request: requestGetFormDetails } = useApi(getFormDetailsHandler.getFormDetails);
  const getFormDetails = async ({
    pdfId,
    applicationId,
  }: {
    pdfId?: string;
    applicationId?: string;
  }): Promise<FormDetails | null> => {
    try {
      let orderFormFromApi: Record<string, any> | null = null;
      const response = await requestGetFormDetails({
        pdfId: pdfId,
        addons: true,
      });
      const productData = response?.data[0];

      // Addons checked by default come from the site-info `checked` flag (PASGF-2744)
      const siteAddons =
        typeof siteData === "object" && siteData !== null ? siteData.addons : undefined;

      let addonsForPdfId = productData.addons ? buildAddons(productData.addons, siteAddons) : [];

      if (applicationId) {
        try {
          orderFormFromApi = await getOrder({
            applicationId,
          });
        } catch (error) {
          log.error({ err: error, module: "useGetFormDetails" }, "errorGetOrder");
        }
        addonsForPdfId = [...addonsForPdfId].sort((a, b) => a.order - b.order);

        // Addons the user already purchased keep their selected state; the rest
        // keep the site-info default checked flag (PASGF-2744)
        const orderActiveAddonIds = new Set(
          orderFormFromApi?.addons?.map((addon: addonProps) => addon.id) ?? []
        );
        addonsForPdfId = addonsForPdfId.map((addon) => ({
          ...addon,
          value: orderActiveAddonIds.has(addon.id) ? "1" : addon.value,
        }));
      }

      // The discount addon only applies to the DS-64 flow when the saved application
      // has submittingNewPassport === "yes" and the site-info has it checked. It never
      // applies to any other form (PASGF-2744).
      const slug = pdfId ? getSlugByPdfId(pdfId) : null;
      const siteDiscountChecked =
        siteAddons?.some(
          (siteAddon: Addon) => siteAddon.identifier === "discount" && siteAddon.checked === true
        ) ?? false;
      const discountEligible =
        slug === "lost-stolen-passport" &&
        orderFormFromApi?.submittingNewPassport === "yes" &&
        siteDiscountChecked;

      addonsForPdfId = addonsForPdfId.map((addon) =>
        addon.identifier === "discount" ? { ...addon, value: discountEligible ? "1" : "0" } : addon
      );

      const formDetails = {
        form: orderFormFromApi,
        addons: addonsForPdfId,
        pdf: productData.product,
        price: productData.product.price?.amount,
        total: productData.product.price?.total,
      };

      return formDetails;
    } catch (e: Error | any) {
      log.error(
        { error: { message: e.message, stack: e.stack }, module: "useGetFormDetails" },
        "errorGetSiteinfo"
      );
      return null; // or throw if you want to propagate
    }
  };

  return {
    getFormDetails,
  };
};

export default useGetFormDetails;
